import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AcceptAccountMemberInvitationSchema,
  AccountMemberInvitationByTokenSchema,
  AccountMemberInviteSchema,
  AccountMemberRoleChangeSchema,
  AccountMemberRoleSchema,
  AccountMemberSchema,
  CreateAccountMemberInviteSchema,
  OKGeneric,
} from '../models';
import { GenericAccountId, GenericUserID, GenericUUID } from '../models/models.generic';

extendZodWithOpenApi(z);
const c = initContract();

export const apiAccountMembers = c.router({
  getAccountMembers: {
    method: 'GET',
    path: `/1.0/accounts/:accountId/members`,
    pathParams: z.object({ accountId: GenericAccountId }),
    description: 'Gets all members on a given account.',
    responses: {
      200: z.array(AccountMemberSchema),
    },
  },
  getAccountMemberInvitations: {
    method: 'GET',
    path: `/1.0/accounts/:accountId/members/invitations`,
    pathParams: z.object({ accountId: GenericAccountId }),
    description: 'Gets all invitations on a given account.',
    responses: {
      200: z.array(AccountMemberInviteSchema),
    },
  },
  getAccountMemberInvitationId: {
    method: 'GET',
    path: `/1.0/accounts/:accountId/members/invitations/:invitationId`,
    pathParams: z.object({
      accountId: GenericAccountId,
      invitationId: GenericUUID.describe('Invitation ID'),
    }),
    description: 'Gets a specific invitation on a given account.',
    responses: {
      200: AccountMemberInviteSchema,
    },
  },
  getAccountMemberInvitationByToken: {
    method: 'GET',
    path: `/1.0/accounts/members/invitations/:token`,
    pathParams: z.object({
      token: z.string().describe('Invitation token for accepting membership'),
    }),
    description: 'Gets a specific invitation on a given account.',
    responses: {
      200: AccountMemberInvitationByTokenSchema,
    },
  },
  acceptAccountMemberInvitation: {
    method: 'POST',
    path: `/1.0/accounts/members/invitations/accept`,
    description: 'Accepts an invitation on a given account.',
    body: z.object({ token: z.string() }),
    responses: {
      200: AcceptAccountMemberInvitationSchema,
    },
  },
  deleteAccountMemberInvitationId: {
    method: 'DELETE',
    path: `/1.0/accounts/:accountId/members/invitations/:invitationId`,
    pathParams: z.object({
      accountId: GenericAccountId,
      invitationId: GenericUUID.describe('Invitation ID'),
    }),
    description: 'Deletes a specific invitation on a given account.',
    body: null,
    responses: {
      200: OKGeneric,
    },
  },
  createAccountMemberInvitation: {
    method: 'POST',
    path: `/1.0/accounts/:accountId/members/invitations`,
    pathParams: z.object({ accountId: GenericAccountId }),
    description: 'Creates an invitation on a given account.',
    body: CreateAccountMemberInviteSchema,
    responses: {
      200: AccountMemberInviteSchema,
    },
  },
  removeAccountMemberId: {
    method: 'DELETE',
    path: `/1.0/accounts/:accountId/members/:userId`,
    pathParams: z.object({
      accountId: GenericAccountId,
      userId: GenericUserID,
    }),
    description:
      'Removes a member from a given account, unassigning any records that persist to the account owner.',
    body: null,
    responses: {
      200: OKGeneric,
    },
  },
  updateAccountMemberRole: {
    method: 'PUT',
    path: `/1.0/accounts/:accountId/members`,
    pathParams: z.object({ accountId: GenericAccountId }),
    description: 'Updates a members role on a given account.',
    body: AccountMemberRoleChangeSchema,
    responses: {
      200: OKGeneric,
    },
  },
  getCurrentMemberRole: {
    method: 'GET',
    path: `/1.0/accounts/:accountId/members/me`,
    pathParams: z.object({ accountId: GenericAccountId }),
    description: 'Gets the authenticated users current role relationship to account.',
    responses: {
      200: AccountMemberRoleSchema,
    },
  },
});
